import React, { useCallback, useMemo, useEffect } from 'react'
import { FaPlus } from 'react-icons/fa'
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table'

import { getEmptyColumnObject } from '../../../utils/submissions/table'

const EditableTable = ({ columns, data, setData, setControlledPageSize, beforePageChange }) => {
    const addRow = useCallback(() => {
        if (setData) {
            const columnObject = getEmptyColumnObject(columns)
            setData(old => {
                return [...old, columnObject]
            })
        }
    }, [setData])

    const canSetData = useMemo(() => (setData ? true : false), [setData])

    const table = useReactTable(
        {
            columns,
            data,
            initialState: { pagination: { pageIndex: 0 } },
            autoResetPageIndex: false,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            meta: { setData, addRow },
        },
    )

    const { pageIndex, pageSize } = table.getState().pagination
    const page = table.getRowModel().rows
    const canPreviousPage = table.getCanPreviousPage()
    const canNextPage = table.getCanNextPage()
    const pageOptions = Array.from({ length: table.getPageCount() }, (_, i) => i)
    const pageCount = table.getPageCount()

    useEffect(() => {
        setControlledPageSize(pageSize)
    }, [pageSize])

    return (
        <React.Fragment>

            <div className="gridWrapper editableTableWrapper">
                <table
                    className="table is-narrow is-fullwidth editableTable uploadTable"
                >
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className="is-size-7"
                                    >
                                        <div className="explorerTableHeader icon-color-3">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {page.map((row, i) => {
                            return (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => {
                                        return (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <div className={`tableFooter ${data.length < pageSize ? 'is-hidden' : ''}`}>
                    <div className="buttons are-small">
                        <button
                            type="button"
                            className={`button is-link`}
                            onClick={() => beforePageChange(() => table.setPageIndex(0))}
                            disabled={!canPreviousPage}
                        >
                            {'<<'}
                        </button>{' '}
                        <button
                            type="button"
                            className={`button is-link`}
                            onClick={() => beforePageChange(() => table.previousPage())}
                            disabled={!canPreviousPage}
                        >
                            {'<'}
                        </button>{' '}
                        <button
                            type="button"
                            className={`button is-link`}
                            onClick={() => beforePageChange(() => table.nextPage())}
                            disabled={!canNextPage}
                        >
                            {'>'}
                        </button>{' '}
                        <button
                            type="button"
                            className={`button is-link`}
                            onClick={() => beforePageChange(() => table.setPageIndex(pageCount - 1))}
                            disabled={!canNextPage}
                        >
                            {'>>'}
                        </button>{' '}
                    </div>
                    <div className="resultsCount margin-left-sm">
                        <span className="margin-right-sm">
                            <strong>{data.length}</strong> Total Results
                        </span>
                        <span className="margin-right-sm">
                            | Page <strong>{pageIndex + 1}</strong> of{' '}
                            <strong>{pageOptions.length}</strong>
                        </span>
                    </div>
                    <div className="pageNavigator">
                        <span>| Go to page:</span>
                        <div className="field is-horizontal">
                            <div className="control">
                                <input
                                    type="number"
                                    className="input is-small margin-left-sm"
                                    value={pageIndex + 1}
                                    onChange={e => {
                                        const p = e.target.value
                                            ? Number(e.target.value) - 1
                                            : 0
                                        return beforePageChange(() => table.setPageIndex(p))
                                    }}
                                    style={{ width: '100px' }}
                                />
                            </div>
                        </div>
                        <div className="field is-horizontal">
                            <div className="control">
                                <div
                                    className={`select is-small margin-left-sm carat-class-3`}
                                >
                                    <select
                                        value={pageSize}
                                        onChange={e => {
                                            table.setPageSize(Number(e.target.value))
                                        }}
                                    >
                                        {[10, 20].map(ps => (
                                            <option key={ps} value={ps}>
                                                Show {ps}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {canSetData ? (
                <div className="newTableRowWrapper">
                    <button
                        type="button"
                        className="button is-small is-link"
                        onClick={() => addRow()}
                    >
                        <span>Add Row</span>
                        <span className="icon is-small">
                            <FaPlus />
                        </span>
                    </button>
                </div>
            ) : null}
        </React.Fragment>
    )
}

export default EditableTable
